import { ArrowBack, CloudUpload, DeleteForever, Edit, Lock, LockOpen } from '@mui/icons-material'
import { Button, Divider, Grid } from '@mui/material'
import { ExplanationAccordion, GreenText } from 'components'
import * as controllers from 'controllers'
import { InboundTripDto, InboundTripPAPSDto, InboundTripPAPSSignedForeignShippersDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import InboundTripPAPSDetailsCard from './InboundTripPAPSDetailsCard'
import ManageVehiclesListCard from './ManageVehiclesListCard'
import { useAuth, useLayout } from 'context'
import PAPSManagementAddEditDialog from '../PAPSManagementAddEditDialog'
import { enqueueSnackbar } from 'notistack'
import SignedForeignShippersUploadDialog from './SignedForeignShippersUploadDialog'

export default function InboundTripsDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const { permissions } = useAuth()

  const { inboundTripGuid, inboundTripPAPSGuid } = useParams()
  const [loadingState, setLoadingState] = useLoadingState({
    isDeletingInboundTripPAPS: false,
    isGettingInboundTrip: false,
    isGettingInboundTripPAPS: false,
    isUpdatingInboundTripPAPS: false,
    isGettingInboundTripPAPSSignedForeignShippers: false,
    isSavingInboundTripPAPSSignedForeignShippers: false
  })
  const [inboundTrip, setInboundTrip] = useState<InboundTripDto>(new InboundTripDto())
  const [inboundTripPAPS, setInboundTripPAPS] = useState<InboundTripPAPSDto>(new InboundTripPAPSDto())
  const [isAddEditInboundTripPAPSDialogOpen, setIsAddEditInboundTripPAPSDialogOpen] = useState<boolean>(false)

  const [isSignedForeignShippersUploadDialogOpen, setIsSignedForeignShippersUploadDialogOpen] = useState<boolean>(false)
  const [inboundTripPAPSSignedForeignShippers, setInboundTripPAPSSignedForeignShippers] = useState<InboundTripPAPSSignedForeignShippersDto>(
    new InboundTripPAPSSignedForeignShippersDto()
  )

  useEffect(() => {
    if (
      inboundTripGuid &&
      inboundTripGuid.toLowerCase() !== 'undefined' &&
      inboundTripGuid.toLowerCase() !== 'new' &&
      inboundTripPAPSGuid &&
      inboundTripPAPSGuid.toLowerCase() !== 'undefined' &&
      inboundTripPAPSGuid.toLowerCase() !== 'new'
    ) {
      // Start loading for inbound trip
      setLoadingState('isGettingInboundTrip', true)

      // Fetching inbound trip data
      controllers
        .getInboundTrip(inboundTripGuid)
        .then(response => {
          setInboundTrip(response.value)
        })
        .catch(error => {
          // Handle the error
          console.error('Failed to fetch inbound trip', error)
        })
        .finally(() => {
          // Stop loading for inbound trip
          setLoadingState('isGettingInboundTrip', false)
        })

      // Start loading for inbound trip PAPS
      setLoadingState('isGettingInboundTripPAPS', true)

      // Fetching inbound trip PAPS data
      controllers
        .getInboundTripPAPSGuid(inboundTripGuid, inboundTripPAPSGuid)
        .then(response => {
          console.log('fetching response: ', response.value)
          setInboundTripPAPS(response.value)
        })
        .catch(error => {
          // Handle the error
          console.error('Failed to fetch inbound trip PAPS', error)
        })
        .finally(() => {
          // Stop loading for inbound trip PAPS
          setLoadingState('isGettingInboundTripPAPS', false)
        })
    }
  }, [inboundTripGuid, inboundTripPAPSGuid])

  useEffect(() => {
    layout.setTitle('Inbound Trip PAPS')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isDeletingInboundTripPAPS || loadingState.isGettingInboundTrip || loadingState.isGettingInboundTripPAPS)
  }, [loadingState.isDeletingInboundTripPAPS, loadingState.isGettingInboundTrip, loadingState.isGettingInboundTripPAPS])

  return (
    <>
      <PAPSManagementAddEditDialog
        isLoading={loadingState.isUpdatingInboundTripPAPS}
        initialValues={inboundTripPAPS}
        inboundTrip={inboundTrip}
        onClose={() => {
          setIsAddEditInboundTripPAPSDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingInboundTripPAPS', true)
          controllers
            .updateInboundTripPAPS(inboundTrip.inboundTripGuid!, values)
            .then(response => {
              enqueueSnackbar('PAPS Saved Successfully!', { variant: 'success' })
              setIsAddEditInboundTripPAPSDialogOpen(false)
              setInboundTripPAPS(response.value)
            })
            .finally(() => {
              setLoadingState('isUpdatingInboundTripPAPS', false)
            })
        }}
        open={isAddEditInboundTripPAPSDialogOpen}
      />

      <SignedForeignShippersUploadDialog
        initialValues={new InboundTripPAPSSignedForeignShippersDto()}
        loading={loadingState.isSavingInboundTripPAPSSignedForeignShippers}
        onClose={() => {
          setIsSignedForeignShippersUploadDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isSavingInboundTripPAPSSignedForeignShippers', true)
          controllers.inboundTrips
            .uploadInboundTripPAPSSignedForeignShippers(inboundTripGuid!, inboundTripPAPSGuid!, values)
            .then(response => {
              enqueueSnackbar('Signed Foreign Shippers Saved Successfully!', { variant: 'success' })
              setInboundTripPAPS(response.value)
              setIsSignedForeignShippersUploadDialogOpen(false)
            })
            .finally(() => {
              setLoadingState('isSavingInboundTripPAPSSignedForeignShippers', false)
            })
        }}
        open={isSignedForeignShippersUploadDialogOpen}
      />

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <InboundTripPAPSDetailsCard inboundTrip={inboundTrip} inboundTripPAPS={inboundTripPAPS}>
            <Grid item>
              <Button
                disabled={loadingState.isUpdatingInboundTripPAPS || loadingState.isGettingInboundTripPAPS}
                onClick={() => {
                  navigate(-1)
                }}
                color='primary'
                startIcon={<ArrowBack />}
                variant='text'
              >
                BACK
              </Button>
            </Grid>

            <Grid item sx={{ alignSelf: 'stretch' }}>
              <Divider orientation='vertical' />
            </Grid>

            <Grid item>
              <Button
                disabled={loadingState.isUpdatingInboundTripPAPS || loadingState.isGettingInboundTripPAPS}
                color='primary'
                endIcon={<CloudUpload />}
                onClick={() => {
                  setIsSignedForeignShippersUploadDialogOpen(true)
                }}
                variant='text'
              >
                UPLOAD
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='error'
                disabled={
                  !permissions.CANCEL_IMPORT_CAPABILITY ||
                  !inboundTrip.inboundTripGuid ||
                  !inboundTripPAPS.inboundTripPAPSGuid ||
                  loadingState.isUpdatingInboundTripPAPS ||
                  inboundTrip.isLocked
                }
                endIcon={<DeleteForever />}
                onClick={() => {
                  if (window.confirm('Deleting a PAPS cannot be undone, and any documents associated with this PAPS will also be deleted from the system. ')) {
                    setLoadingState('isDeletingInboundTripPAPS', true)
                    controllers
                      .deleteInboundTripPAPS(inboundTrip.inboundTripGuid!, inboundTripPAPS.inboundTripPAPSGuid!)
                      .then(() => {
                        enqueueSnackbar('PAPS Deleted Successfully!', { variant: 'success' })
                        navigate('/inbound-trips/' + inboundTrip.inboundTripGuid)
                      })
                      .finally(() => {
                        setLoadingState('isDeletingInboundTripPAPS', false)
                      })
                  }
                }}
                variant='text'
              >
                DELETE PAPS
              </Button>
            </Grid>

            <Grid item>
              <Button
                disabled={inboundTrip.isLocked || inboundTripPAPS.numberOfVehiclesInPAPS === 0 || loadingState.isUpdatingInboundTripPAPS}
                endIcon={inboundTripPAPS.isLocked ? <Lock /> : <LockOpen />}
                onClick={() => {
                  setLoadingState('isUpdatingInboundTripPAPS', true)
                  const wasLocked = inboundTripPAPS.isLocked
                  setInboundTripPAPS({ ...inboundTripPAPS, isLocked: !wasLocked })

                  if (wasLocked) {
                    controllers.inboundTrips
                      .unlockInboundTripPAPS(inboundTrip.inboundTripGuid!, inboundTripPAPS.inboundTripPAPSGuid!)
                      .then(response => {
                        setInboundTripPAPS(response.value)
                        enqueueSnackbar('PAPS Unlocked Successfully!', { variant: 'success' })
                      })
                      .catch(() => {
                        setInboundTripPAPS({ ...inboundTripPAPS, isLocked: wasLocked })
                        enqueueSnackbar('Failed to unlock PAPS', { variant: 'error' })
                      })
                      .finally(() => {
                        setLoadingState('isUpdatingInboundTripPAPS', false)
                      })
                  } else {
                    controllers.inboundTrips
                      .lockInboundTripPAPS(inboundTrip.inboundTripGuid!, inboundTripPAPS.inboundTripPAPSGuid!)
                      .then(response => {
                        setInboundTripPAPS(response.value)
                        enqueueSnackbar('PAPS Locked Successfully!', { variant: 'success' })
                      })
                      .catch(() => {
                        setInboundTripPAPS({ ...inboundTripPAPS, isLocked: wasLocked })
                        enqueueSnackbar('Failed to lock PAPS', { variant: 'error' })
                      })
                      .finally(() => {
                        setLoadingState('isUpdatingInboundTripPAPS', false)
                      })
                  }
                }}
                variant='text'
              >
                {loadingState.isUpdatingInboundTripPAPS ? (
                  inboundTripPAPS.isLocked ? (
                    'Locking PAPS...'
                  ) : (
                    'Unlocking PAPS...'
                  )
                ) : inboundTripPAPS.isLocked ? (
                  'UNLOCK PAPS'
                ) : (
                  'LOCK PAPS'
                )}
              </Button>
            </Grid>

            <Grid item>
              <Button
                disabled={inboundTripPAPS.isLocked}
                endIcon={<Edit />}
                onClick={() => {
                  setIsAddEditInboundTripPAPSDialogOpen(true)
                }}
                variant='text'
                size='small'
              >
                EDIT
              </Button>
            </Grid>
          </InboundTripPAPSDetailsCard>
        </Grid>

        <Grid item xs={12}>
          <ManageVehiclesListCard inboundTrip={inboundTrip} inboundTripPAPS={inboundTripPAPS} setInboundTripPAPS={setInboundTripPAPS} />
        </Grid>

        <Grid item xs={12}>
          <GreenText>
            {`**Hero Card:**

The hero card displays the following fields: PAPS #, Exporter Company, Buyer Company (left blank if no buyer was selected), Vehicle Destination Company, Vehicle Destination Company Address, Title Destination Company, Title Destination Company Address, Pro Forma, Foreign Shippers, and Signed Foreign Shippers.

**Lock PAPS:**

The Lock PAPS button can only be selected if at least one VIN exists within the PAPS.

When Lock PAPS is pressed, the Edit button on the PAPS card becomes disabled. Additionally, within the Manage Vehicles card, the Remove Selected and Add Vehicles buttons are disabled, along with all individual vehicle checkboxes.

Pressing Lock PAPS also triggers the system to generate the documents that will be sent to the broker and inbound transportation company for that PAPS. The generated documents include:

   - Pro Forma Invoice (one per PAPS, listing all vehicles in the PAPS)
   - Foreign Shippers document (one per PAPS, listing only vehicles with VINs that begin with 1, 4, 5, or 7)

**Delete PAPS:**

“Delete PAPS” can only be pressed by users with the “Cancel Import Capability” VTS2 Access user permission. When “Delete PAPS” is pressed, a warning will be displayed saying, “Deleting a PAPS cannot be undone, and any documents associated with this PAPS will also be deleted from the system.”

**Manage Vehicles:**

To add vehicles to a PAPS, users press the Add Vehicles button, which opens the Add Vehicles dialog.

To remove vehicles from a PAPS, users check the box in the row of each VIN they wish to remove and then press the Remove Selected button.

The # of Vehicles label is a calculated value originating from this screen and represents the total number of VINs in the PAPS. This value populates the # of Vehicles field on the Pro Forma Invoice for the PAPS.

The Invoice Total label is a calculated value representing the sum of all Amount (USD) values for VINs in the PAPS. This value populates the Invoice Total field on the Pro Forma Invoice.

The Total Weight (lbs.) label is a calculated value representing the combined Curb Weight for all VINs in the PAPS. This value populates the Total Weight lbs. field on the Pro Forma Invoice.

The Gross Weight (Kg) label is a calculated value representing the combined GVWR for all VINs in the PAPS. This value populates the Gross Weight Kg. field on the Pro Forma Invoice.

The table of VINs added to the PAPS displays the following columns:

   - Checkbox for selecting a vehicle
   - Exporter
   - Buyer
   - VIN
   - Year
   - Make
   - Model
   - Amount (USD)
   - Curb Weight
   - GVWR
   - HS-7
   - EPA 3520
   - Bond

The HS-7, EPA 3520, and Bond columns remain empty until the PAPS is locked.`}
          </GreenText>
        </Grid>
      </Grid>
    </>
  )
}
